/**
 * Custom Features - Merged JavaScript
 * 
 * This file combines all custom feature functionality:
 * 1. Image Protection
 * 2. Coupon Banner
 * 3. Delivery Time Block
 * 4. Product Detail Pop
 * 
 * Optimized: Unified initialization system, merged MutationObserver
 */

// ============================================
// IMMEDIATE PLACEHOLDER FUNCTION CREATOR
// ============================================
// 在脚本加载时立即创建占位函数处理器
// 这必须在 IIFE 之前执行，确保在脚本 defer 加载时也能立即创建函数

// 使用全局拦截器，在脚本加载前就捕获所有函数调用
(function() {
  'use strict';
  
  // 存储所有待创建的占位函数
  const placeholderRegistry = {};
  
  // 创建一个通用的占位函数处理器
  // 这个函数会在真正的函数创建之前拦截所有调用
  function createGenericPlaceholder(productId) {
    const functionName = 'openProductDetailPop' + productId;
    if (!window[functionName]) {
      window[functionName] = function() {
        // 如果真正的函数已经存在，直接调用
        if (window[functionName] && window[functionName]._isRealFunction) {
          return window[functionName].apply(this, arguments);
        }
        
        // 否则，尝试初始化并重试
        const modalId = 'productDetailPop-' + productId;
        let modal = document.getElementById(modalId);
        
        if (modal) {
          // 弹窗已存在，初始化并打开
          const radarDataScript = document.getElementById('productDetailPopRadarData-' + productId);
          let radarData = null;
          if (radarDataScript) {
            try {
              radarData = JSON.parse(radarDataScript.textContent);
            } catch (e) {
              console.error('Failed to parse radar data:', e);
            }
          }
          
          // 如果 window.initProductDetailPop 存在，调用它
          if (typeof window.initProductDetailPop === 'function') {
            window.initProductDetailPop(modalId, radarData);
            // 再次尝试调用函数
            if (window[functionName] && window[functionName]._isRealFunction) {
              return window[functionName].apply(this, arguments);
            }
          }
          
          // 如果函数仍然不存在，直接打开弹窗
          if (modal) {
            modal.style.display = 'flex';
            modal.setAttribute('aria-hidden', 'false');
            document.body.style.overflow = 'hidden';
          }
        } else {
          // 弹窗还未加载，等待并重试
          let retryCount = 0;
          const maxRetries = 20;
          const retryInterval = setInterval(function() {
            retryCount++;
            const retryModal = document.getElementById(modalId);
            if (retryModal) {
              clearInterval(retryInterval);
              const radarDataScript = document.getElementById('productDetailPopRadarData-' + productId);
              let radarData = null;
              if (radarDataScript) {
                try {
                  radarData = JSON.parse(radarDataScript.textContent);
                } catch (e) {
                  console.error('Failed to parse radar data:', e);
                }
              }
              if (typeof window.initProductDetailPop === 'function') {
                window.initProductDetailPop(modalId, radarData);
                if (window[functionName] && window[functionName]._isRealFunction) {
                  window[functionName].apply(this, arguments);
                } else if (retryModal) {
                  retryModal.style.display = 'flex';
                  retryModal.setAttribute('aria-hidden', 'false');
                  document.body.style.overflow = 'hidden';
                }
              }
            } else if (retryCount >= maxRetries) {
              clearInterval(retryInterval);
              console.warn('Product detail pop not available for product:', productId);
            }
          }, 100);
        }
      };
    }
  }
  
  // 使用 Proxy 拦截所有对 window.openProductDetailPop* 的访问
  // 这样即使函数不存在，也能被捕获并创建占位函数
  const originalWindow = window;
  const handler = {
    get: function(target, prop) {
      // 如果属性以 openProductDetailPop 开头且是数字结尾
      if (typeof prop === 'string' && prop.startsWith('openProductDetailPop')) {
        const match = prop.match(/^openProductDetailPop(\d+)$/);
        if (match && match[1]) {
          const productId = match[1];
          // 如果函数不存在，立即创建占位函数
          if (!target[prop] || !target[prop]._isRealFunction) {
            createGenericPlaceholder(productId);
          }
        }
      }
      // 返回原始属性值
      return target[prop];
    },
    set: function(target, prop, value) {
      // 允许正常设置属性
      target[prop] = value;
      return true;
    }
  };
  
  // 由于不能直接替换 window，我们使用一个包装器
  // 在访问 window.openProductDetailPop* 时自动创建占位函数
  function setupGlobalInterceptor() {
    // 重写所有可能的 openProductDetailPop* 函数访问
    // 使用 Object.defineProperty 为每个可能的产品 ID 创建 getter
    // 但这样效率不高，所以我们使用另一种方法：
    // 在每次访问时动态创建
  }
  
  // 立即扫描并创建所有占位函数（在脚本加载时）
  // 这个函数必须在脚本的最开始执行
  function scanAndCreatePlaceholdersImmediate() {
    try {
      // 如果 DOM 已经准备好，扫描所有按钮和弹窗
      if (document.body) {
        // 扫描所有按钮
        try {
          const buttons = document.querySelectorAll('[onclick*="openProductDetailPop"]');
          buttons.forEach(function(button) {
            try {
              const onclickAttr = button.getAttribute('onclick');
              if (onclickAttr && typeof onclickAttr === 'string' && onclickAttr.includes('openProductDetailPop')) {
                const match = onclickAttr.match(/openProductDetailPop(\d+)/);
                if (match && match[1]) {
                  createGenericPlaceholder(match[1]);
                }
              }
            } catch (e) {
              // 忽略单个按钮的错误
            }
          });
        } catch (e) {
          // 忽略查询错误
        }
        
        // 扫描所有弹窗
        try {
          const modals = document.querySelectorAll('[id^="productDetailPop-"]');
          modals.forEach(function(modal) {
            try {
              const modalId = modal.id;
              if (modalId && modalId.startsWith('productDetailPop-')) {
                const productId = modalId.replace('productDetailPop-', '');
                if (productId) {
                  createGenericPlaceholder(productId);
                }
              }
            } catch (e) {
              // 忽略单个弹窗的错误
            }
          });
        } catch (e) {
          // 忽略查询错误
        }
      }
    } catch (e) {
      // 忽略扫描错误
    }
  }
  
  // 使用 MutationObserver 监听所有对 window 属性的访问
  // 但由于无法直接拦截 window 属性访问，我们使用另一种方法：
  // 在脚本加载时立即扫描，并在 DOM 准备好后再次扫描
  // 同时，使用一个全局的 getter 函数来拦截访问
  
  // 立即执行扫描（在脚本加载时）
  if (document.body) {
    scanAndCreatePlaceholdersImmediate();
  }
  
  // 如果 DOM 还在加载，等待 DOMContentLoaded 后再扫描一次
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      scanAndCreatePlaceholdersImmediate();
      // 延迟再次扫描，确保所有动态内容都已加载
      setTimeout(scanAndCreatePlaceholdersImmediate, 100);
    }, { once: true });
  } else {
    // DOM 已准备好，立即扫描
    scanAndCreatePlaceholdersImmediate();
    // 延迟再次扫描，确保所有动态内容都已加载
    setTimeout(scanAndCreatePlaceholdersImmediate, 100);
  }
  
  // 将函数暴露到全局，供后续使用
  window._createGenericPlaceholder = createGenericPlaceholder;
  window._scanAndCreatePlaceholdersImmediate = scanAndCreatePlaceholdersImmediate;
  
  // 创建一个全局的拦截器，在访问不存在的函数时自动创建
  // 由于无法直接拦截 window 属性访问，我们使用一个包装函数
  // 但更好的方法是：在 HTML 解析时就能捕获到按钮的 onclick 属性
  // 所以我们使用 MutationObserver 来监听 DOM 变化
  if (typeof MutationObserver !== 'undefined') {
    const placeholderObserver = new MutationObserver(function(mutations) {
      let shouldScan = false;
      for (let i = 0; i < mutations.length; i++) {
        const mutation = mutations[i];
        for (let j = 0; j < mutation.addedNodes.length; j++) {
          const node = mutation.addedNodes[j];
          if (node.nodeType === 1) {
            // 检查是否有新的按钮或弹窗
            if (node.hasAttribute && node.hasAttribute('onclick')) {
              const onclickAttr = node.getAttribute('onclick');
              if (onclickAttr && typeof onclickAttr === 'string' && onclickAttr.includes('openProductDetailPop')) {
                shouldScan = true;
                break;
              }
            } else if (node.id && typeof node.id === 'string' && node.id.startsWith('productDetailPop-')) {
              shouldScan = true;
              break;
            } else if (node.querySelector) {
              if (node.querySelector('[onclick*="openProductDetailPop"]') || node.querySelector('[id^="productDetailPop-"]')) {
                shouldScan = true;
                break;
              }
            }
          }
          if (shouldScan) break;
        }
        if (shouldScan) break;
      }
      
      if (shouldScan) {
        // 立即扫描并创建占位函数（同步执行，不能延迟）
        scanAndCreatePlaceholdersImmediate();
      }
    });
    
    // 开始观察
    if (document.body) {
      placeholderObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    } else {
      // 如果 body 还不存在，等待 DOMContentLoaded
      document.addEventListener('DOMContentLoaded', function() {
        placeholderObserver.observe(document.body, {
          childList: true,
          subtree: true
        });
      }, { once: true });
    }
  }
})();

(function() {
  'use strict';

  // ============================================
  // UNIFIED INITIALIZATION SYSTEM
  // ============================================
  
  // 统一的 DOM 就绪处理队列
  const initQueue = [];
  let domReady = false;

  function addInit(fn) {
    if (domReady) {
      fn();
    } else {
      initQueue.push(fn);
    }
  }

  function runInitQueue() {
    domReady = true;
    initQueue.forEach(function(fn) {
      try {
        fn();
      } catch (e) {
        console.error('Init function error:', e);
      }
    });
    initQueue.length = 0;
  }

  // DOM 就绪检测
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runInitQueue);
  } else {
    runInitQueue();
  }

  // ============================================
  // 1. IMAGE PROTECTION
  // ============================================
  
  function generateToken() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
  }
  
  function addWatermark(canvas, ctx) {
    var watermarkText = 'copyright © www.ceayabikes.com';
    ctx.textBaseline = 'middle';
    
    function measureText(fontSize) {
      ctx.font = 'bold ' + fontSize + 'px Arial';
      return ctx.measureText(watermarkText).width;
    }
    
    function calculateOptimalFontSize(maxWidth, maxHeight) {
      var fontSize = Math.min(maxWidth / watermarkText.length * 1.5, maxHeight / 2, 50);
      var measured = measureText(fontSize);
      while (measured > maxWidth * 0.9 && fontSize > 10) {
        fontSize -= 2;
        measured = measureText(fontSize);
      }
      return Math.max(fontSize, 12);
    }
    
    var patternSize = Math.min(canvas.width / 2.5, canvas.height / 2.5, 180);
    var patternFontSize = calculateOptimalFontSize(patternSize * 0.85, patternSize * 0.35);
    var angle = -Math.PI / 6;
    var offsets = [0, patternSize / 2];
    
    ctx.save();
    ctx.font = 'bold ' + patternFontSize + 'px Arial';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.lineWidth = Math.max(1.5, patternFontSize / 15);
    ctx.textAlign = 'center';
    
    for (var i = 0; i < offsets.length; i++) {
      for (var j = 0; j < offsets.length; j++) {
        for (var x = offsets[i]; x < canvas.width + patternSize; x += patternSize) {
          for (var y = offsets[j]; y < canvas.height + patternSize; y += patternSize) {
            ctx.save();
            ctx.translate(x, y);
            ctx.rotate(angle);
            ctx.strokeText(watermarkText, 0, 0);
            ctx.fillText(watermarkText, 0, 0);
            ctx.restore();
          }
        }
      }
    }
    ctx.restore();
    
    var centerFontSize = calculateOptimalFontSize(canvas.width * 0.7, canvas.height * 0.25);
    ctx.save();
    ctx.font = 'bold ' + centerFontSize + 'px Arial';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.75)';
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.65)';
    ctx.lineWidth = Math.max(2.5, centerFontSize / 12);
    ctx.textAlign = 'center';
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(angle);
    ctx.strokeText(watermarkText, 0, 0);
    ctx.fillText(watermarkText, 0, 0);
    ctx.restore();
  }
  
  function createWatermarkedImage(img, callback) {
    if (!img) {
      callback(null);
      return;
    }
    
    function processImage(sourceImg) {
      try {
        var canvas = document.createElement('canvas');
        var ctx = canvas.getContext('2d');
        var width = sourceImg.naturalWidth || sourceImg.width;
        var height = sourceImg.naturalHeight || sourceImg.height;
        
        if (width <= 0 || height <= 0) {
          callback(null);
          return;
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(sourceImg, 0, 0, width, height);
        addWatermark(canvas, ctx);
        canvas.toBlob(function(blob) {
          callback(blob || null);
        }, 'image/png', 0.95);
      } catch (e) {
        callback(null);
      }
    }
    
    if (img.complete && img.naturalWidth > 0) {
      processImage(img);
    } else {
      var imgSrc = img.src || img.currentSrc || img.dataset.src;
      if (!imgSrc) {
        callback(null);
        return;
      }
      var watermarkImg = new Image();
      watermarkImg.crossOrigin = 'anonymous';
      watermarkImg.onload = function() {
        processImage(watermarkImg);
      };
      watermarkImg.onerror = function() {
        if (img.complete && img.naturalWidth > 0) {
          processImage(img);
        } else {
          callback(null);
        }
      };
      watermarkImg.src = imgSrc;
    }
  }
  
  function downloadWatermarkedImage(img) {
    createWatermarkedImage(img, function(blob) {
      if (!blob) return;
      
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = 'image-' + generateToken() + '.png';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(function() {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
    });
  }
  
  function preventAllDrag(e) {
    var target = e.target;
    var isImg = target.tagName === 'IMG';
    var hasImg = target.closest && target.closest('img');
    var containsImg = target.querySelector && target.querySelector('img');
    
    if (isImg || hasImg || containsImg || target.tagName === 'PICTURE' || 
        (target.tagName === 'A' && (target.href && (target.href.match(/\.(webp|jpg|jpeg|png|gif)/i) || target.querySelector('img'))))) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'none';
        e.dataTransfer.dropEffect = 'none';
      }
      return false;
    }
  }
  
  function disableImageDrag(img) {
    if (!img || img.tagName !== 'IMG') return;
    img.setAttribute('draggable', 'false');
    img.style.webkitUserDrag = 'none';
    img.ondragstart = function() { return false; };
    var link = img.closest && img.closest('a');
    if (link) {
      link.setAttribute('draggable', 'false');
      link.ondragstart = function() { return false; };
    }
  }
  
  function initAllImages() {
    // 使用 requestIdleCallback 优化性能，避免阻塞图片加载
    if (window.requestIdleCallback) {
      requestIdleCallback(function() {
        document.querySelectorAll('img').forEach(disableImageDrag);
        document.querySelectorAll('a').forEach(function(link) {
          if (link.querySelector('img') || (link.href && link.href.match(/\.(webp|jpg|jpeg|png|gif)/i))) {
            link.setAttribute('draggable', 'false');
            link.ondragstart = function() { return false; };
          }
        });
      }, { timeout: 1000 });
    } else {
      // 降级方案：延迟执行，避免阻塞
      setTimeout(function() {
        document.querySelectorAll('img').forEach(disableImageDrag);
        document.querySelectorAll('a').forEach(function(link) {
          if (link.querySelector('img') || (link.href && link.href.match(/\.(webp|jpg|jpeg|png|gif)/i))) {
            link.setAttribute('draggable', 'false');
            link.ondragstart = function() { return false; };
          }
        });
      }, 100);
    }
  }

  // 初始化图片保护
  addInit(function() {
    initAllImages();
    
    document.addEventListener('dragstart', preventAllDrag, true);
    document.addEventListener('drag', preventAllDrag, true);
    document.addEventListener('dragend', preventAllDrag, true);
    
    document.addEventListener('mousedown', function(e) {
      var img = e.target.tagName === 'IMG' ? e.target : (e.target.closest && e.target.closest('img'));
      if (img) disableImageDrag(img);
    }, true);
    
    var touchData = { time: 0, img: null, x: 0, y: 0, moved: false, link: null, preventClick: false };
    
    document.addEventListener('touchstart', function(e) {
      var img = e.target.tagName === 'IMG' ? e.target : (e.target.closest && e.target.closest('img'));
      var link = img && img.closest ? img.closest('a') : null;
      if (img && (img.src || img.currentSrc || img.dataset.src)) {
        touchData.time = Date.now();
        touchData.img = img;
        touchData.link = link;
        touchData.moved = false;
        touchData.preventClick = false;
        if (e.touches && e.touches.length > 0) {
          touchData.x = e.touches[0].clientX;
          touchData.y = e.touches[0].clientY;
        }
      } else {
        touchData.img = null;
        touchData.link = null;
      }
    }, { passive: true });
    
    document.addEventListener('touchmove', function(e) {
      if (touchData.img && e.touches && e.touches.length > 0) {
        if (Math.abs(e.touches[0].clientX - touchData.x) > 10 || 
            Math.abs(e.touches[0].clientY - touchData.y) > 10) {
          touchData.moved = true;
        }
      }
    }, { passive: true });
    
    document.addEventListener('touchend', function(e) {
      if (touchData.img && !touchData.moved) {
        var touchDuration = Date.now() - touchData.time;
        var link = touchData.link;
        if (touchDuration > 800) {
          e.preventDefault();
          e.stopPropagation();
          touchData.preventClick = true;
          downloadWatermarkedImage(touchData.img);
          if (link) {
            link.style.pointerEvents = 'none';
            setTimeout(function() {
              link.style.pointerEvents = '';
            }, 100);
          }
        }
      }
      touchData.img = null;
      touchData.link = null;
      touchData.moved = false;
    }, { passive: false });
    
    document.addEventListener('click', function(e) {
      if (touchData.preventClick) {
        e.preventDefault();
        e.stopPropagation();
        touchData.preventClick = false;
      }
    }, true);
    
    document.addEventListener('contextmenu', function(e) {
      var img = e.target.tagName === 'IMG' ? e.target : (e.target.closest && e.target.closest('img'));
      var link = img && img.closest ? img.closest('a') : null;
      if (img || link) {
        e.preventDefault();
        if (img && (img.src || img.currentSrc || img.dataset.src)) {
          downloadWatermarkedImage(img);
        }
      }
    }, true);
  });

  // ============================================
  // 2. COUPON BANNER
  // ============================================
  
  // Countdown timer functionality
  class CouponTimer {
    constructor(element) {
      this.element = element;
      this.couponBanner = element.closest('.coupon-banner');
      this.endDate = new Date(element.dataset.endDate);
      this.daysEl = element.querySelector('.js-timer-days');
      this.hoursEl = element.querySelector('.js-timer-hours');
      
      if (this.isValid()) {
        this.init();
      } else {
        this.onExpire();
      }
    }

    isValid() {
      return !isNaN(this.endDate.getTime()) && this.endDate > new Date();
    }

    init() {
      this.update();
      this.interval = setInterval(() => this.update(), 1000);
    }

    update() {
      const now = new Date();
      const diff = this.endDate - now;

      if (diff <= 0) {
        this.onExpire();
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

      if (this.daysEl) this.daysEl.textContent = String(days).padStart(2, '0');
      if (this.hoursEl) this.hoursEl.textContent = String(hours).padStart(2, '0');
    }

    onExpire() {
      if (this.interval) {
        clearInterval(this.interval);
      }
      if (this.couponBanner) {
        this.couponBanner.classList.add('is-expired');
      }
    }
  }

  // 初始化优惠券横幅功能
  addInit(function() {
    // Toggle expand/collapse functionality
    const toggleButtons = document.querySelectorAll('.js-toggle-button');
    toggleButtons.forEach(button => {
      button.addEventListener('click', function() {
        const couponBanner = this.closest('.coupon-banner');
        const expandable = couponBanner?.querySelector('.js-expandable');
        const allToggles = couponBanner?.querySelectorAll('.js-toggle-button');
        
        if (!expandable) return;
        
        const isExpanded = expandable.classList.contains('is-expanded');
        
        if (isExpanded) {
          expandable.classList.remove('is-expanded');
          allToggles?.forEach(toggle => toggle.classList.remove('is-expanded'));
        } else {
          expandable.classList.add('is-expanded');
          allToggles?.forEach(toggle => toggle.classList.add('is-expanded'));
        }
      });
    });

    // Initialize countdown timers
    const timerElements = document.querySelectorAll('.js-timer');
    timerElements.forEach(element => {
      new CouponTimer(element);
    });

    // Handle copy icon button - reference native discount-code.js implementation
    const copyButtons = document.querySelectorAll('.coupon-banner__copy-icon.js-copy-button');
    copyButtons.forEach(button => {
      const couponBanner = button.closest('.coupon-banner');
      const discountCodeElement = couponBanner?.querySelector('.discount-code .js-discount-code');
      const successMessage = couponBanner?.querySelector('.discount-code .js-copy-success');
      
      // Check if clipboard API is available (same as native discount-code.js)
      if (navigator.clipboard && navigator.clipboard.writeText) {
        button.addEventListener('click', function() {
          // Get code from text content (same as native: this.discountCode.textContent.trim())
          const discountCode = discountCodeElement?.textContent.trim();
          if (!discountCode) return;
          
          navigator.clipboard.writeText(discountCode).then(
            () => {
              // Show success message (same as native implementation)
              if (successMessage) {
                successMessage.classList.add('is-visible');
                successMessage.setAttribute('aria-hidden', 'false');
                
                setTimeout(() => {
                  successMessage.classList.add('is-closing');
                }, 1800);
                
                setTimeout(() => {
                  successMessage.classList.remove('is-closing');
                  successMessage.classList.remove('is-visible');
                }, 2000);
              }
            },
            () => {
              // Error handling (same as native: alert(theme.strings.discountCopyFail))
              const theme = window.theme || {};
              const alertMsg = theme?.strings?.discountCopyFail || 'Failed to copy discount code';
              alert(alertMsg); // eslint-disable-line
            }
          );
        });
      } else {
        // Hide button if clipboard API is not available (same as native: this.discountCodeBtn.hidden = true)
        button.hidden = true;
      }
    });
  });

  // ============================================
  // 3. DELIVERY TIME BLOCK
  // ============================================
  
  // 初始化 tooltip 功能
  function initDeliveryTimeTooltip() {
    const triggers = document.querySelectorAll('.delivery-time__tooltip-trigger');
    
    if (triggers.length === 0) {
      // 如果还没有元素，等待一下再试（用于动态加载的内容）
      setTimeout(initDeliveryTimeTooltip, 100);
      return;
    }
    
    triggers.forEach(function(trigger) {
      // 如果已经初始化过，跳过
      if (trigger.dataset.tooltipInitialized === 'true') {
        return;
      }
      trigger.dataset.tooltipInitialized = 'true';
      
      // 查找 tooltip - 优先在 trigger 内部查找
      let tooltip = trigger.querySelector('.delivery-time__tooltip');
      
      // 如果找不到，在整个文档中查找（可能被移到了其他地方）
      if (!tooltip) {
        const block = trigger.closest('.delivery-time-block');
        if (block) {
          tooltip = block.querySelector('.delivery-time__tooltip');
        }
      }
      
      // 如果还是找不到，跳过这个 trigger
      if (!tooltip) {
        console.warn('Tooltip not found for trigger:', trigger);
        return;
      }
      
      // 初始化这个 tooltip
      initTooltip(trigger, tooltip);
    });
  }
  
  // 初始化单个 tooltip
  function initTooltip(trigger, tooltip) {
    // 在事件处理时实时检测，而不是在初始化时固定
    function getIsMobile() {
      const width = window.innerWidth;
      const isMobile = width <= 768;
      return isMobile;
    }
    
    // 桌面端移除关闭按钮（如果存在）
    const existingCloseBtn = tooltip.querySelector('.delivery-time__tooltip-close');
    if (existingCloseBtn) {
      existingCloseBtn.remove();
    }
    
    // 打开 tooltip
    function openTooltip() {
      const isMobile = getIsMobile();

      // 关闭其他 tooltip
      document.querySelectorAll('.delivery-time__tooltip-trigger.active').forEach(function(t) {
        if (t !== trigger) {
          t.classList.remove('active');
        }
      });
      document.querySelectorAll('.delivery-time__tooltip.active').forEach(function(t) {
        if (t !== tooltip) {
          t.classList.remove('active');
        }
      });

      trigger.classList.add('active');
      tooltip.classList.add('active');

      if (isMobile) {
        // 移动端：底部抽屉方案
        if (tooltip.parentNode !== document.body) {
          document.body.appendChild(tooltip);
        }
        
        tooltip.classList.add('active');
        
        // 创建关闭按钮（移动端需要）
        let closeBtn = tooltip.querySelector('.delivery-time__tooltip-close');
        if (!closeBtn) {
          closeBtn = document.createElement('button');
          closeBtn.className = 'delivery-time__tooltip-close';
          closeBtn.type = 'button';
          closeBtn.setAttribute('aria-label', 'Close');
          closeBtn.textContent = 'Close';
          const content = tooltip.querySelector('.delivery-time__tooltip-content');
          if (content) {
            content.appendChild(closeBtn);
          }
        }

        if (closeBtn) {
          closeBtn.addEventListener('click', closeBtnClickHandler);
        }

        // 创建背景遮罩
        let overlay = document.querySelector('.delivery-time-tooltip-overlay');
        if (!overlay) {
          overlay = document.createElement('div');
          overlay.className = 'delivery-time-tooltip-overlay overlay--drawer';
          document.body.appendChild(overlay);
        }

        overlay.style.pointerEvents = 'auto';
        overlay.style.opacity = '1';
        
        tooltip.style.cssText = 'position: fixed !important; top: auto !important; bottom: 0 !important; left: 0 !important; right: 0 !important; width: 100% !important; max-width: 100% !important; height: calc(100vh - 100px) !important; max-height: calc(100vh - 100px) !important; transform: translate3d(0, 100%, 0) !important; opacity: 0 !important; visibility: visible !important; pointer-events: none !important; z-index: 40 !important; border-radius: 1.5rem 1.5rem 0 0 !important; transition: transform 0.7s cubic-bezier(0.19, 1, 0.22, 1), opacity 0.3s ease-in-out, visibility 0s 0.7s !important; display: block !important; background-color: #ffffff !important; color: #000000 !important; overflow: auto !important; box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.15) !important;';
        
        // 触发动画
        requestAnimationFrame(function() {
          requestAnimationFrame(function() {
            tooltip.style.cssText = 'position: fixed !important; top: auto !important; bottom: 0 !important; left: 0 !important; right: 0 !important; width: 100% !important; max-width: 100% !important; height: calc(100vh - 100px) !important; max-height: calc(100vh - 100px) !important; transform: translate3d(0, 0, 0) !important; opacity: 1 !important; visibility: visible !important; pointer-events: auto !important; z-index: 40 !important; border-radius: 1.5rem 1.5rem 0 0 !important; transition: transform 0.7s cubic-bezier(0.19, 1, 0.22, 1), opacity 0.3s ease-in-out, visibility 0s 0s !important; display: block !important; background-color: #ffffff !important; color: #000000 !important; overflow: auto !important; box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.15) !important;';
          });
        });
      } else {
        // 桌面端：将tooltip移到body下，避免z-index限制
        if (tooltip.parentNode !== document.body) {
          document.body.appendChild(tooltip);
        }
        
        // 桌面端：移除关闭按钮（如果存在）
        const existingCloseBtn = tooltip.querySelector('.delivery-time__tooltip-close');
        if (existingCloseBtn) {
          existingCloseBtn.remove();
        }
        
        // 获取trigger的位置
        const triggerRect = trigger.getBoundingClientRect();
        const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
        const scrollY = window.pageYOffset || document.documentElement.scrollTop;
        
        // 计算tooltip位置（相对于trigger）
        const tooltipTop = triggerRect.bottom + scrollY + 8;
        const tooltipLeft = triggerRect.left + scrollX + (triggerRect.width / 2);
        
        // 设置tooltip样式
        tooltip.style.cssText = 'display: block !important; opacity: 1 !important; visibility: visible !important; pointer-events: auto !important; position: fixed !important; top: ' + (triggerRect.bottom + 8) + 'px !important; left: ' + tooltipLeft + 'px !important; transform: translateX(-50%) !important; z-index: 999999 !important; width: 400px !important; max-width: calc(100vw - 2rem) !important; min-width: 300px !important; background: transparent !important;';
        
        // 确保content有白色背景和高z-index
        const content = tooltip.querySelector('.delivery-time__tooltip-content');
        if (content) {
          content.style.cssText = 'background: #ffffff !important; position: relative !important; padding: 1.25rem !important; border-radius: 0.5rem !important; box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15) !important; border: 1px solid rgba(0, 0, 0, 0.1) !important; width: 100% !important; box-sizing: border-box !important; z-index: 999999 !important;';
        }
        
        // 检查tooltip是否会超出屏幕，如果会则调整位置
        requestAnimationFrame(function() {
          const rect = tooltip.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          
          if (rect.right > viewportWidth) {
            tooltip.style.left = (triggerRect.right - 10) + 'px';
            tooltip.style.transform = 'translateX(-100%)';
          } else if (rect.left < 0) {
            tooltip.style.left = (triggerRect.left + 10) + 'px';
            tooltip.style.transform = 'none';
          }
          if (rect.bottom > viewportHeight) {
            tooltip.style.top = (triggerRect.top - rect.height - 8) + 'px';
          }
        });
      }
    }
    
    // 关闭 tooltip
    function closeTooltip() {
      const isMobile = getIsMobile();

      trigger.classList.remove('active');
      tooltip.classList.remove('active');

      if (isMobile) {
        // 移动端：向下滑出
        tooltip.style.cssText = 'position: fixed !important; top: auto !important; bottom: 0 !important; left: 0 !important; right: 0 !important; width: 100% !important; max-width: 100% !important; height: calc(100vh - 100px) !important; max-height: calc(100vh - 100px) !important; transform: translate3d(0, 100%, 0) !important; opacity: 0 !important; visibility: hidden !important; pointer-events: none !important; z-index: 40 !important; border-radius: 1.5rem 1.5rem 0 0 !important; transition: transform 0.7s cubic-bezier(0.19, 1, 0.22, 1), opacity 0.3s ease-in-out, visibility 0s 0.7s !important; display: block !important; background-color: #ffffff !important; color: #000000 !important; overflow: auto !important; box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.15) !important;';
        
        // 隐藏背景遮罩
        const overlay = document.querySelector('.delivery-time-tooltip-overlay');
        if (overlay) {
          overlay.style.opacity = '0';
          overlay.style.pointerEvents = 'none';
        }
        
        setTimeout(function() {
          if (!tooltip.classList.contains('active')) {
            tooltip.style.display = 'none';
          }
        }, 700);
      } else {
        // 桌面端：立刻隐藏
        tooltip.style.cssText = 'display: none !important; opacity: 0 !important; visibility: hidden !important; pointer-events: none !important;';
      }
    }
    
    // 事件处理函数
    function clickHandler(e) {
      const isMobile = getIsMobile();
      if (isMobile) {
        e.preventDefault();
        e.stopPropagation();
        if (trigger.classList.contains('active')) {
          closeTooltip();
        } else {
          openTooltip();
        }
      } else {
        e.preventDefault();
        e.stopPropagation();
        if (trigger.classList.contains('active')) {
          closeTooltip();
        } else {
          openTooltip();
        }
      }
    }
    
    function mouseEnterHandler(e) {
      const isMobile = getIsMobile();
      if (!isMobile && !trigger.classList.contains('active')) {
        openTooltip();
      }
    }
    
    function mouseLeaveHandler(e) {
      const isMobile = getIsMobile();
      if (!isMobile) {
        setTimeout(() => {
          if (!trigger.matches(':hover') && !tooltip.matches(':hover')) {
            closeTooltip();
          }
        }, 100);
      }
    }
    
    function documentClickHandler(e) {
      const isMobile = getIsMobile();
      if (isMobile && trigger.classList.contains('active')) {
        const overlay = document.querySelector('.delivery-time-tooltip-overlay');
        if (overlay && e.target === overlay) {
          closeTooltip();
        } else if (!trigger.contains(e.target) && !tooltip.contains(e.target)) {
          closeTooltip();
        }
      }
    }
    
    function keyDownHandler(e) {
      if (e.key === 'Escape' && trigger.classList.contains('active')) {
        closeTooltip();
      }
    }
    
    function closeBtnClickHandler(e) {
      e.preventDefault();
      e.stopPropagation();
      closeTooltip();
    }
    
    // 事件监听
    trigger.addEventListener('click', clickHandler);
    trigger.addEventListener('mouseenter', mouseEnterHandler);
    trigger.addEventListener('mouseleave', mouseLeaveHandler);
    tooltip.addEventListener('mouseenter', mouseEnterHandler);
    tooltip.addEventListener('mouseleave', mouseLeaveHandler);
    document.addEventListener('click', documentClickHandler, true);
    document.addEventListener('keydown', keyDownHandler);
    
    // 设置样式
    const initialIsMobile = getIsMobile();
    trigger.style.cursor = initialIsMobile ? 'pointer' : 'help';
    trigger.style.pointerEvents = 'auto';
  }
  
  // 初始化 delivery time tooltip
  addInit(function() {
    setTimeout(initDeliveryTimeTooltip, 50);
  });

  // ============================================
  // 4. PRODUCT DETAIL POP
  // ============================================
  
  // 全局变量：跟踪已加载的资源
  let chartJsLoaded = false;
  let chartJsLoading = false;

  function loadChartJS() {
    return new Promise(function(resolve, reject) {
      if (window.Chart) {
        chartJsLoaded = true;
        resolve();
        return;
      }

      if (chartJsLoading) {
        const checkInterval = setInterval(function() {
          if (window.Chart) {
            clearInterval(checkInterval);
            chartJsLoaded = true;
            resolve();
          }
        }, 50);
        return;
      }

      chartJsLoading = true;
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.min.js';
      script.async = true;
      script.onload = function() {
        chartJsLoaded = true;
        chartJsLoading = false;
        resolve();
      };
      script.onerror = function() {
        chartJsLoading = false;
        reject(new Error('Failed to load Chart.js'));
      };
      document.head.appendChild(script);
    });
  }

  function initRadarChart(radarCanvas, radarData) {
    if (!radarData || !radarData.hasRadar) return;

    if (!radarCanvas) return;

    // 检查是否已经初始化过
    if (radarCanvas.chartInstance) {
      return;
    }

    const labels = radarData.labels || [];
    const dataValues = radarData.data || [];
    const minLength = Math.min(labels.length, dataValues.length);
    const chartLabels = labels.slice(0, minLength);
    
    // 验证并转换数据为数字
    const chartData = dataValues.slice(0, minLength).map(function(val) {
      const strVal = String(val).trim();
      if (strVal === '' || isNaN(strVal)) {
        console.warn('Invalid radar data value (non-numeric):', val);
        return 0;
      }
      const numVal = parseFloat(strVal);
      if (isNaN(numVal) || !isFinite(numVal)) {
        console.warn('Invalid radar data value (not a finite number):', val);
        return 0;
      }
      return numVal;
    });

    if (chartLabels.length === 0 || chartData.length === 0) return;

    const ctx = radarCanvas.getContext('2d');
    
    try {
      const radarChartInstance = new Chart(ctx, {
        type: 'radar',
        data: {
          labels: chartLabels,
          datasets: [{
            label: radarData.label || 'Product Performance',
            data: chartData,
            backgroundColor: 'rgba(59, 130, 246, 0.2)',
            borderColor: 'rgba(59, 130, 246, 1)',
            pointBackgroundColor: 'rgba(59, 130, 246, 1)',
            pointBorderColor: '#fff',
            pointHoverBackgroundColor: '#fff',
            pointHoverBorderColor: 'rgba(59, 130, 246, 1)',
            pointRadius: 4,
            pointBorderWidth: 2
          }]
        },
        options: {
          scales: {
            r: {
              beginAtZero: true,
              max: 10,
              ticks: {
                stepSize: 2,
                backdropColor: 'transparent'
              },
              grid: {
                color: 'rgba(0, 0, 0, 0.05)'
              },
              angleLines: {
                color: 'rgba(0, 0, 0, 0.05)'
              },
              pointLabels: {
                font: {
                  size: 12,
                  weight: '600'
                },
                color: '#4b5563'
              }
            }
          },
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              titleColor: '#1f2937',
              bodyColor: '#4b5563',
              borderColor: '#e5e7eb',
              borderWidth: 1,
              padding: 12,
              boxPadding: 6,
              usePointStyle: true,
              callbacks: {
                label: function(context) {
                  return context.label + ': ' + context.raw;
                }
              }
            }
          },
          elements: {
            line: {
              borderWidth: 2
            }
          },
          maintainAspectRatio: false
        }
      });
      
      radarCanvas.chartInstance = radarChartInstance;
    } catch (error) {
      console.error('Failed to initialize radar chart:', error);
    }
  }

  function applyRedStrikethrough(modal) {
    const priceContainer = modal.querySelector('.product-detail-pop-price');
    if (priceContainer) {
      const strikethroughElements = priceContainer.querySelectorAll('s, del, .price--compare, .price__compare, .price-item--compare, .price-item--regular, .price_was, [class*="compare"]:not([class*="current"]), [class*="regular"]:not([class*="current"]), [class*="was"]:not([class*="current"])');
      strikethroughElements.forEach(function(el) {
        if (!el.classList.contains('price_current') && !el.classList.contains('price-current') && !el.closest('.price_current') && !el.closest('.price-current')) {
          el.style.textDecoration = 'line-through';
          el.style.textDecorationColor = '#e53e3e';
          el.style.webkitTextDecorationColor = '#e53e3e';
          el.style.textDecorationThickness = '2px';
          el.style.webkitTextDecorationThickness = '2px';
        }
      });
      
      const currentPriceElements = priceContainer.querySelectorAll('.price_current, .price-current, [class*="current"]:not([class*="compare"]):not([class*="was"])');
      currentPriceElements.forEach(function(el) {
        el.style.textDecoration = 'none';
      });
    }
  }

  function initProductDetailPop(modalId, radarData) {
    const modal = document.getElementById(modalId);
    if (!modal) return;

    const closeButtons = modal.querySelectorAll('.product-detail-pop-close, .product-detail-pop-close-btn');
    const overlay = modal;
    let keyDownHandler = null;
    
    function closeModal() {
      modal.style.display = 'none';
      modal.setAttribute('aria-hidden', 'true');
      
      if (keyDownHandler) {
        document.removeEventListener('keydown', keyDownHandler, true);
        keyDownHandler = null;
      }
      
      const otherOpenModals = document.querySelectorAll('.drawer[open], .modal:not([style*="display: none"]), .overlay.is-visible, [role="dialog"][aria-hidden="false"]:not(#' + modalId + ')');
      if (otherOpenModals.length === 0) {
        document.body.style.overflow = '';
      }
      
      const triggerButton = document.querySelector('[onclick*="openProductDetailPop' + modalId.replace('productDetailPop-', '') + '"]');
      if (triggerButton) {
        triggerButton.focus();
      }
    }

    // 优化图片加载：立即加载弹窗内的所有图片
    function preloadImages() {
      const images = modal.querySelectorAll('img[loading="lazy"], img[data-src], img[data-srcset]');
      images.forEach(function(img) {
        // 移除 lazy loading，立即加载
        if (img.hasAttribute('loading')) {
          img.removeAttribute('loading');
        }
        
        // 如果有 data-src，立即设置为 src
        if (img.hasAttribute('data-src')) {
          img.src = img.getAttribute('data-src');
          img.removeAttribute('data-src');
        }
        
        // 如果有 data-srcset，立即设置为 srcset
        if (img.hasAttribute('data-srcset')) {
          img.srcset = img.getAttribute('data-srcset');
          img.removeAttribute('data-srcset');
        }
        
        // 强制加载图片（即使已经在 DOM 中）
        if (img.src && !img.complete) {
          const tempImg = new Image();
          tempImg.src = img.src;
          if (img.srcset) {
            tempImg.srcset = img.srcset;
          }
        }
      });
    }
    
    function openModal() {
      // 显示加载状态
      modal.classList.add('is-loading');
      modal.style.display = 'flex';
      modal.setAttribute('aria-hidden', 'false');
      
      const otherOpenModals = document.querySelectorAll('.drawer[open], .modal:not([style*="display: none"]), .overlay.is-visible, [role="dialog"][aria-hidden="false"]:not(#' + modalId + ')');
      if (otherOpenModals.length === 0) {
        document.body.style.overflow = 'hidden';
      }
      
      // 使用 requestAnimationFrame 确保过渡效果流畅
      requestAnimationFrame(function() {
        // 预加载图片
        preloadImages();
        
        // 等待一帧后移除加载状态，显示内容
        requestAnimationFrame(function() {
          modal.classList.remove('is-loading');
          modal.classList.add('is-loaded');
          
          // 应用红色删除线
          setTimeout(function() {
            applyRedStrikethrough(modal);
          }, 50);
          
          // 加载雷达图（如果需要）
          if (radarData && radarData.hasRadar) {
            const radarChartId = radarData.chartId;
            const radarCanvas = document.getElementById(radarChartId);
            
            if (window.Chart) {
              initRadarChart(radarCanvas, radarData);
            } else {
              loadChartJS().then(function() {
                initRadarChart(radarCanvas, radarData);
              }).catch(function(error) {
                console.error('Failed to load Chart.js:', error);
              });
            }
          }
        });
      });
      
      if (!keyDownHandler) {
        keyDownHandler = function(e) {
          if (e.key === 'Escape' && modal.style.display === 'flex') {
            const otherOpenModals = document.querySelectorAll('.drawer[open], .modal:not([style*="display: none"]), .overlay.is-visible, [role="dialog"][aria-hidden="false"]:not(#' + modalId + ')');
            if (otherOpenModals.length === 0) {
              e.stopPropagation();
              closeModal();
            }
          }
        };
        document.addEventListener('keydown', keyDownHandler, true);
      }
    }

    closeButtons.forEach(function(btn) {
      btn.addEventListener('click', closeModal);
    });

    overlay.addEventListener('click', function(e) {
      if (e.target === overlay) {
        closeModal();
      }
    });

    const openFunctionName = 'openProductDetailPop' + modalId.replace('productDetailPop-', '');
    // 标记这是真正的函数，不是占位函数
    openModal._isRealFunction = true;
    window[openFunctionName] = openModal;
  }
  
  // 将 initProductDetailPop 暴露到全局，供占位函数使用
  window.initProductDetailPop = initProductDetailPop;

  // 优化：不再在页面加载时初始化所有弹窗
  // 改为只在点击时才初始化，减少页面加载负担
  function initAllProductDetailPops() {
    // 不再自动初始化，改为按需初始化
    // 这个函数保留是为了兼容性，但不会自动调用
  }

  // 初始化 product detail pop
  // 使用多种方式确保函数在按钮被点击前已定义
  
  // 立即创建占位函数的辅助函数
  function createPlaceholderForProduct(productId) {
    if (!productId) {
      console.warn('createPlaceholderForProduct: productId is empty');
      return;
    }
    
    // 使用全局的占位函数创建器（如果存在）
    if (window._createGenericPlaceholder) {
      window._createGenericPlaceholder(productId);
      return;
    }
    
    const functionName = 'openProductDetailPop' + productId;
    // 如果函数已存在且是真正的函数，则不覆盖
    if (window[functionName] && window[functionName]._isRealFunction) {
      return;
    }
    
    if (!window[functionName]) {
      window[functionName] = function() {
        const modalId = 'productDetailPop-' + productId;
        let modal = document.getElementById(modalId);
        
        if (modal) {
          // 弹窗已存在，初始化并打开
          const radarDataScript = document.getElementById('productDetailPopRadarData-' + productId);
          let radarData = null;
          if (radarDataScript) {
            try {
              radarData = JSON.parse(radarDataScript.textContent);
            } catch (e) {
              console.error('Failed to parse radar data:', e);
            }
          }
          initProductDetailPop(modalId, radarData);
          // 调用真正的函数
          if (window[functionName]) {
            window[functionName]();
          }
        } else {
          // 弹窗还未加载，等待并重试
          let retryCount = 0;
          const maxRetries = 15;
          const retryInterval = setInterval(function() {
            retryCount++;
            const retryModal = document.getElementById(modalId);
            if (retryModal) {
              clearInterval(retryInterval);
              const radarDataScript = document.getElementById('productDetailPopRadarData-' + productId);
              let radarData = null;
              if (radarDataScript) {
                try {
                  radarData = JSON.parse(radarDataScript.textContent);
                } catch (e) {
                  console.error('Failed to parse radar data:', e);
                }
              }
              initProductDetailPop(modalId, radarData);
              if (window[functionName]) {
                window[functionName]();
              }
            } else if (retryCount >= maxRetries) {
              clearInterval(retryInterval);
              console.warn('Product detail pop not available for product:', productId);
            }
          }, 100);
        }
      };
    }
  }
  
  // 立即扫描并创建所有占位函数（在脚本加载时）
  // 这是一个轻量级操作（只是创建函数引用），同步执行不会阻塞页面加载
  function scanAndCreatePlaceholders() {
    try {
      // 扫描所有按钮（包括已存在的和动态添加的）
      const buttons = document.querySelectorAll('[onclick*="openProductDetailPop"]');
      buttons.forEach(function(button) {
        try {
          const onclickAttr = button.getAttribute('onclick');
          if (onclickAttr && typeof onclickAttr === 'string' && onclickAttr.includes('openProductDetailPop')) {
            const match = onclickAttr.match(/openProductDetailPop(\d+)/);
            if (match && match[1]) {
              createPlaceholderForProduct(match[1]);
            }
          }
        } catch (e) {
          console.warn('Error creating placeholder for button:', e);
        }
      });
      
      // 也扫描所有弹窗，提前创建函数
      const modals = document.querySelectorAll('[id^="productDetailPop-"]');
      modals.forEach(function(modal) {
        try {
          const modalId = modal.id;
          if (modalId && modalId.startsWith('productDetailPop-')) {
            const productId = modalId.replace('productDetailPop-', '');
            if (productId) {
              createPlaceholderForProduct(productId);
            }
          }
        } catch (e) {
          console.warn('Error creating placeholder for modal:', e);
        }
      });
    } catch (e) {
      console.error('Error in scanAndCreatePlaceholders:', e);
    }
  }
  
  // 立即执行（在脚本加载时）- 必须立即同步创建占位函数，不能延迟
  // 占位函数创建必须同步执行，确保在按钮的 onclick 被解析前就存在
  // 这是一个轻量级操作（只是创建函数引用），不会阻塞页面加载
  scanAndCreatePlaceholders();
  
  // 优化：不在页面加载时初始化弹窗，只在点击时才初始化
  // 这样可以减少页面加载时的负担，提升加载速度
  function ensureProductDetailPopInitialized(modalId) {
    // 只初始化指定的弹窗，而不是所有弹窗
    if (modalId) {
      const modal = document.getElementById(modalId);
      if (modal && !modal.dataset.initialized) {
        const productId = modalId.replace('productDetailPop-', '');
        const radarDataScript = document.getElementById('productDetailPopRadarData-' + productId);
        let radarData = null;
        if (radarDataScript) {
          try {
            radarData = JSON.parse(radarDataScript.textContent);
          } catch (e) {
            console.error('Failed to parse radar data:', e);
          }
        }
        initProductDetailPop(modalId, radarData);
        modal.dataset.initialized = 'true';
      }
    }
  }
  
  // 只在 DOM 就绪时创建占位函数，不初始化弹窗
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      scanAndCreatePlaceholders();
    });
  } else {
    scanAndCreatePlaceholders();
  }

  // ============================================
  // UNIFIED MUTATION OBSERVER
  // ============================================
  
  // 统一的 MutationObserver 用于监听动态内容
  // 使用防抖优化性能，避免频繁触发
  if (typeof MutationObserver !== 'undefined') {
    let debounceTimer = null;
    let pendingDeliveryTime = false;
    let pendingProductDetail = false;
    
    const observer = new MutationObserver(function(mutations) {
      // 使用防抖，避免频繁执行
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      
      // 快速检查是否有相关元素
      let shouldInitDeliveryTime = false;
      let shouldInitProductDetail = false;
      let hasNewButtons = false;
      
      // 限制检查的 mutations 数量，避免性能问题
      const maxMutations = 50;
      const mutationsToCheck = mutations.slice(0, maxMutations);
      
      for (let i = 0; i < mutationsToCheck.length; i++) {
        const mutation = mutationsToCheck[i];
        const addedNodes = mutation.addedNodes;
        
        for (let j = 0; j < addedNodes.length; j++) {
          const node = addedNodes[j];
          if (node.nodeType !== 1) continue;
          
          // 跳过购物车相关元素，避免影响购物车性能
          if (node.classList && (
            node.classList.contains('drawer') ||
            node.classList.contains('cart-drawer') ||
            node.classList.contains('quick-add-drawer') ||
            (node.id && typeof node.id === 'string' && node.id.includes('cart'))
          )) {
            continue;
          }
          
          // 检查是否有新的 delivery-time tooltip trigger
          if (!shouldInitDeliveryTime) {
            if (node.classList && node.classList.contains('delivery-time__tooltip-trigger')) {
              shouldInitDeliveryTime = true;
            } else if (node.querySelector && node.querySelector('.delivery-time__tooltip-trigger')) {
              shouldInitDeliveryTime = true;
            }
          }
          
          // 检查是否有新的 product detail pop，并立即创建占位函数
          if (!shouldInitProductDetail) {
            if (node.id && typeof node.id === 'string' && node.id.startsWith('productDetailPop-')) {
              shouldInitProductDetail = true;
              // 立即创建占位函数（同步执行）
              const productId = node.id.replace('productDetailPop-', '');
              createPlaceholderForProduct(productId);
            } else if (node.querySelector && node.querySelector('[id^="productDetailPop-"]')) {
              shouldInitProductDetail = true;
              // 立即为新弹窗创建占位函数
              const modals = node.querySelectorAll('[id^="productDetailPop-"]');
              modals.forEach(function(modal) {
                const productId = modal.id.replace('productDetailPop-', '');
                createPlaceholderForProduct(productId);
              });
            }
          }
          
          // 检查是否有新的按钮，并立即创建占位函数
          if (!hasNewButtons) {
            if (node.hasAttribute && node.hasAttribute('onclick')) {
              const onclickAttr = node.getAttribute('onclick');
              if (onclickAttr && typeof onclickAttr === 'string' && onclickAttr.includes('openProductDetailPop')) {
                hasNewButtons = true;
                // 立即创建占位函数（同步执行，不能延迟）
                const match = onclickAttr.match(/openProductDetailPop(\d+)/);
                if (match) {
                  createPlaceholderForProduct(match[1]);
                }
              }
            } else if (node.querySelector && node.querySelector('[onclick*="openProductDetailPop"]')) {
              hasNewButtons = true;
              // 立即为新按钮创建占位函数
              const buttons = node.querySelectorAll('[onclick*="openProductDetailPop"]');
              buttons.forEach(function(button) {
                const onclickAttr = button.getAttribute('onclick');
                if (onclickAttr && typeof onclickAttr === 'string' && onclickAttr.includes('openProductDetailPop')) {
                  const match = onclickAttr.match(/openProductDetailPop(\d+)/);
                  if (match) {
                    createPlaceholderForProduct(match[1]);
                  }
                }
              });
            }
          }
          
          // 如果都找到了，提前退出
          if (shouldInitDeliveryTime && shouldInitProductDetail && hasNewButtons) {
            break;
          }
        }
        
        if (shouldInitDeliveryTime && shouldInitProductDetail && hasNewButtons) {
          break;
        }
      }
      
      // 使用防抖延迟执行，避免频繁初始化
      debounceTimer = setTimeout(function() {
        if (shouldInitDeliveryTime || pendingDeliveryTime) {
          pendingDeliveryTime = false;
          setTimeout(initDeliveryTimeTooltip, 50);
        }
        
        // 优化：不再自动初始化弹窗，只创建占位函数
        // 弹窗将在点击时才初始化，减少页面加载负担
        if (shouldInitProductDetail || hasNewButtons) {
          // 只创建占位函数，不初始化
          scanAndCreatePlaceholders();
        }
      }, 100);
      
      // 记录待处理的状态
      if (shouldInitDeliveryTime) pendingDeliveryTime = true;
      if (shouldInitProductDetail || hasNewButtons) pendingProductDetail = true;
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

})();